import PropTypes from "prop-types";
import React, { useMemo, useState, useCallback } from "react";
import CustomInputField from "../ui/inputs/CustomInputField";
import LocationSelector from "../LocationSelector";
import SortDropdown from "../ui/dropdawn/SortDropdown";
import { usePhoneCountryCodes } from "../../hooks/userComplementaryQuery";
import { 
  EPS_OPTIONS, 
  searchInOptions, 
  getPaginatedOptions,
  DEFAULT_DROPDOWN_CONFIG 
} from "../../lib/userProfileData";
import "../../assets/scss/components/ContactSectionAddPersons.scss"

export default function ContactSectionAddPersons({
  contactInfo,
  onContactInfoChange,
  onLocationSelect,
  theme,
}) {
  const { data: countryCodes, isLoading: isLoadingCountryCodes } = usePhoneCountryCodes();
  const countryCodesData = countryCodes?.data || [];
  
  // Estados para manejo de búsquedas en dropdowns
  const [epsSearch, setEpsSearch] = useState({
    searchTerm: '',
    currentPage: 1,
    isLoading: false
  });

  // Configuración para dropdown de EPS con búsqueda
  const epsOptions = useMemo(() => {
    const { options } = getPaginatedOptions(
      EPS_OPTIONS,
      epsSearch.currentPage,
      DEFAULT_DROPDOWN_CONFIG.itemsPerPage,
      epsSearch.searchTerm
    );
    return options;
  }, [epsSearch.searchTerm, epsSearch.currentPage]);

  const epsTotalItems = useMemo(() => {
    return searchInOptions(EPS_OPTIONS, epsSearch.searchTerm).length;
  }, [epsSearch.searchTerm]);

  // Handlers existentes
  const handlePrimaryPhoneCountryChange = (value) => {
    onContactInfoChange({
      target: {
        name: "primaryPhoneCountryId",
        value,
      },
    });
  };

  const handleSecondaryPhoneCountryChange = (value) => {
    onContactInfoChange({
      target: {
        name: "secondaryPhoneCountryId",
        value,
      },
    });
  };

  // NUEVOS HANDLERS
  const handleEpsChange = (value) => {
    onContactInfoChange({
      target: {
        name: "eps",
        value,
      },
    });
  };

  // Handlers para búsqueda en EPS
  const handleEpsSearch = useCallback((searchTerm) => {
    setEpsSearch(prev => ({
      ...prev,
      searchTerm,
      currentPage: 1,
      isLoading: false
    }));
  }, []);

  const handleEpsPageChange = useCallback((page) => {
    setEpsSearch(prev => ({
      ...prev,
      currentPage: page
    }));
  }, []);

  // Validación de teléfono (solo números)
  const handlePhoneChange = useCallback((e) => {
    const { name, value } = e.target;
    // Permitir solo números, espacios, guiones y paréntesis
    const phoneRegex = /^[0-9\s\-\(\)]*$/;
    if (phoneRegex.test(value) || value === '') {
      onContactInfoChange(e);
    }
  }, [onContactInfoChange]);

  return (
    <div className={`GM__${theme}-contact-section`}>
      <h2 className={`GM__${theme}-section-title`}>Información de contacto</h2>

      {/* Sección principal de contacto */}
      <div className={`GM__${theme}-contact-row`}>
        <div className={`GM__${theme}-phone-group`}>
          <div className={`GM__${theme}-input-group`}>
            <label htmlFor="primaryPhone" className={`GM__${theme}-input-label`}>
              Número de Celular Principal
            </label>
            <div className={`GM__${theme}-phone-input-container`}>
              <SortDropdown
                id="primaryPhoneCountry"
                value={contactInfo.primaryPhoneCountryId || ""}
                onChange={handlePrimaryPhoneCountryChange}
                options={
                  !isLoadingCountryCodes && countryCodesData
                    ? countryCodesData.map((country) => ({
                        value: country.id,
                        label: `${country.country_code} (${country.country_name})`,
                        country_code: country.country_code,
                      }))
                    : []
                }
                type="phone"
                theme={theme}
                placeholder="Seleccione código"
                isLoading={isLoadingCountryCodes}
              />
              <CustomInputField
                id="primaryPhone"
                type="text"
                name="primaryPhoneNumber"
                value={contactInfo.primaryPhoneNumber}
                onChange={handlePhoneChange}
                placeholder="300 123 4567"
                theme={theme}
              />
            </div>
          </div>
        </div>

        <div className={`GM__${theme}-phone-group`}>
          <div className={`GM__${theme}-input-group`}>
            <label htmlFor="secondaryPhone" className={`GM__${theme}-input-label`}>
              Número de Celular Secundario (Opcional)
            </label>
            <div className={`GM__${theme}-phone-input-container`}>
              <SortDropdown
                id="secondaryPhoneCountry"
                value={contactInfo.secondaryPhoneCountryId || ""}
                onChange={handleSecondaryPhoneCountryChange}
                options={
                  !isLoadingCountryCodes && countryCodesData
                    ? countryCodesData.map((country) => ({
                        value: country.id,
                        label: `${country.country_code} (${country.country_name})`,
                        country_code: country.country_code,
                      }))
                    : []
                }
                type="phone"
                theme={theme}
                placeholder="Seleccione código"
                isLoading={isLoadingCountryCodes}
              />
              <CustomInputField
                id="secondaryPhone"
                type="text"
                name="secondaryPhoneNumber"
                value={contactInfo.secondaryPhoneNumber}
                onChange={handlePhoneChange}
                placeholder="601 234 5678"
                theme={theme}
              />
            </div>
          </div>
        </div>

        <div className={`GM__${theme}-input-group GM__${theme}-email-group`}>
          <label htmlFor="email" className={`GM__${theme}-input-label`}>
            Correo Electrónico
          </label>
          <CustomInputField
            id="email"
            type="email"
            name="email"
            value={contactInfo.email}
            onChange={onContactInfoChange}
            placeholder="usuario@ejemplo.com"
            theme={theme}
          />
        </div>
      </div>

      {/* NUEVA SECCIÓN: Contacto de emergencia */}
      <div className={`GM__${theme}-emergency-contact-section`}>
        <h3 className={`GM__${theme}-subsection-title`}>Contacto de Emergencia</h3>
        <div className={`GM__${theme}-emergency-row`}>
          <div className={`GM__${theme}-input-group`}>
            <label htmlFor="emergencyContactName" className={`GM__${theme}-input-label`}>
              Nombre Completo del Contacto
            </label>
            <CustomInputField
              id="emergencyContactName"
              type="text"
              name="emergencyContactName"
              value={contactInfo.emergencyContactName}
              onChange={onContactInfoChange}
              placeholder="Ej. María Elena Rodríguez Pérez"
              theme={theme}
            />
          </div>
          
          <div className={`GM__${theme}-input-group`}>
            <label htmlFor="emergencyContactPhone" className={`GM__${theme}-input-label`}>
              Teléfono del Contacto
            </label>
            <CustomInputField
              id="emergencyContactPhone"
              type="text"
              name="emergencyContactPhone"
              value={contactInfo.emergencyContactPhone}
              onChange={handlePhoneChange}
              placeholder="300 987 6543"
              theme={theme}
            />
          </div>
        </div>
      </div>

      {/* NUEVA SECCIÓN: Información de salud y seguridad social */}
      <div className={`GM__${theme}-health-section`}>
        <h3 className={`GM__${theme}-subsection-title`}>Información de Salud y Seguridad Social</h3>
        <div className={`GM__${theme}-health-row`}>
          <div className={`GM__${theme}-input-group`}>
            <label htmlFor="eps" className={`GM__${theme}-input-label`}>
              EPS (Entidad Promotora de Salud)
            </label>
            <SortDropdown
              value={contactInfo.eps || ""}
              onChange={handleEpsChange}
              options={epsOptions}
              placeholder="Seleccione EPS"
              theme={theme}
              enableSearch={true}
              enablePagination={true}
              onSearch={handleEpsSearch}
              searchPlaceholder="Buscar EPS..."
              itemsPerPage={DEFAULT_DROPDOWN_CONFIG.itemsPerPage}
              totalItems={epsTotalItems}
              currentPage={epsSearch.currentPage}
              onPageChange={handleEpsPageChange}
              searchTerm={epsSearch.searchTerm}
              isLoading={epsSearch.isLoading}
            />
          </div>
          
          <div className={`GM__${theme}-input-group`}>
            <label htmlFor="arl" className={`GM__${theme}-input-label`}>
              ARL (Administradora de Riesgos Laborales)
            </label>
            <CustomInputField
              id="arl"
              type="text"
              name="arl"
              value={contactInfo.arl}
              onChange={onContactInfoChange}
              placeholder="Ej. SURA ARL, Positiva, AXA Colpatria"
              theme={theme}
            />
          </div>
        </div>
      </div>

      {/* Sección de ubicación existente */}
      <div className={`GM__${theme}-location-section`}>
        <LocationSelector
          onLocationSelect={onLocationSelect}
          locationData={contactInfo.location}
          theme={theme}
        />
      </div>
    </div>
  );
}

ContactSectionAddPersons.propTypes = {
  contactInfo: PropTypes.shape({
    primaryPhoneNumber: PropTypes.string,
    primaryPhoneCountryId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    secondaryPhoneNumber: PropTypes.string,
    secondaryPhoneCountryId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    email: PropTypes.string,
    contactName: PropTypes.string,
    location: PropTypes.object,
    // NUEVOS CAMPOS
    emergencyContactName: PropTypes.string,
    emergencyContactPhone: PropTypes.string,
    eps: PropTypes.string,
    arl: PropTypes.string,
  }).isRequired,
  onContactInfoChange: PropTypes.func.isRequired,
  onLocationSelect: PropTypes.func.isRequired,
  theme: PropTypes.oneOf(['dark', 'light']).isRequired,
};